function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';
import React from 'react';
import { SpinnerReact } from 'common/components/spinner';
import { SpinnerSize } from 'common/components/spinner/types';
import { addToast } from 'common/components/toast-area/toast-manager';
import { ToastType } from 'common/components/toast/types';
import { useFeatureFlagsApi } from 'iron/apps/feature-flags/src/api/feature-flags';
import { useFeatureFlagStore } from 'iron/apps/feature-flags/src/store';
import { FeatureFlagApiResponse } from 'app_api/projects/feature_flags/types';
import { useTranslation } from 'common/i18n/hooks';
import { payloadParamsFromModel } from 'iron/apps/feature-flags/src/components/util';
import { trackFeatureFlagViewPageAction, ViewPageTrackingEvent } from 'iron/apps/feature-flags/src/tracking';
import { FeatureFlagPageProvider } from '../context';
import { FeatureFlagPageConfiguration } from './configuration';
import { FeatureFlagHeader } from './header';
import styles from './index.module.styl';
import { FeatureFlagPageMain } from './main';
interface FeatureFlagPageProps {
  projectId: number;
  workspaceId: number;
  userId: number;
}
export function FeatureFlagPage(_ref: FeatureFlagPageProps) {
  let {
    projectId,
    workspaceId,
    userId
  }: FeatureFlagPageProps = _ref;
  const {
    flagId
  } = useParams();
  const i18n = useTranslation();
  const {
    getFeatureFlag,
    updateFeatureFlag
  } = useFeatureFlagsApi({
    projectId
  });
  const setCurrentFeatureFlag = useFeatureFlagStore(state => state.setCurrentFeatureFlag);
  const {
    isSuccess,
    data: flag
  } = useQuery({
    queryKey: ["featureFlag", projectId, flagId],
    queryFn: () => {
      if (flagId) {
        return getFeatureFlag({
          id: flagId
        });
      } else {
        throw new Error("Feature flag ID is required");
      }
    }
  });
  const handleUpdateFlag = (updatedFlag: FeatureFlagApiResponse) => {
    const payload = payloadParamsFromModel(updatedFlag);
    setCurrentFeatureFlag(updatedFlag);
    updateFeatureFlag(_objectSpread({
      id: updatedFlag.id
    }, payload)).then(() => {
      addToast({
        message: i18n.t("feature-flags.page.update.success"),
        type: ToastType.SUCCESS,
        timeout: 6000
      });
    }).catch(error => {
      addToast({
        message: i18n.t("feature-flags.page.update.error"),
        type: ToastType.ERROR,
        timeout: 6000
      });
      setCurrentFeatureFlag(updatedFlag); // revert back to previous
      console.error("Error updating feature flag:", error);
    });
  };
  React.useEffect(() => {
    if (isSuccess && flag) {
      setCurrentFeatureFlag(flag);
    }
    trackFeatureFlagViewPageAction({
      event: ViewPageTrackingEvent.PageLoaded,
      featureFlag: flag,
      props: {
        isSuccess
      }
    });
  }, [isSuccess, flag, setCurrentFeatureFlag]);
  if (!flag) return <SpinnerReact size={SpinnerSize.Large} />;
  return <FeatureFlagPageProvider projectId={projectId} workspaceId={workspaceId} userId={userId} featureFlagId={flagId} data-sentry-element="FeatureFlagPageProvider" data-sentry-component="FeatureFlagPage" data-sentry-source-file="index.tsx">
      <div className={styles.wrapper}>
        <FeatureFlagHeader onUpdateFlag={handleUpdateFlag} data-sentry-element="FeatureFlagHeader" data-sentry-source-file="index.tsx" />
        <div className={styles.wrapperContent}>
          <FeatureFlagPageMain projectId={projectId} workspaceId={workspaceId} onUpdateFlag={handleUpdateFlag} data-sentry-element="FeatureFlagPageMain" data-sentry-source-file="index.tsx" />
          <FeatureFlagPageConfiguration data-sentry-element="FeatureFlagPageConfiguration" data-sentry-source-file="index.tsx" />
        </div>
      </div>
    </FeatureFlagPageProvider>;
}